import InfoHand from "@/assets/img/soulmate/info-hand.png"
import InfoHeart from "@/assets/img/soulmate/info-heart.png"
import InfoScale from "@/assets/img/soulmate/info-scale.png"
import InfoSun from "@/assets/img/soulmate/info-sun.png"
import Struggle1 from "@/assets/img/soulmate/struggle-1.png"
import Struggle2 from "@/assets/img/soulmate/struggle-2.png"
import Struggle3 from "@/assets/img/soulmate/struggle-3.png"
import Struggle4 from "@/assets/img/soulmate/struggle-4.png"
import Struggle5 from "@/assets/img/soulmate/struggle-5.png"
import Struggle6 from "@/assets/img/soulmate/struggle-6.png"

import { ANALYTICS_EVENTS } from "@/features/analytics"
import { isFilled, type QuizAnswerValue } from "../components/steps/step-contract"
import { SoulmateChoiceStep } from "../components/steps/soulmate-choice-step"
import { SoulmateConfirmStep } from "../components/steps/soulmate-confirm-step"
import { SoulmateCoverStep } from "../components/steps/soulmate-cover-step"
import { SoulmateEmailStep } from "../components/steps/soulmate-email-step"
import { SoulmateInterstitialStep } from "../components/steps/soulmate-interstitial-step"
import { SoulmateLoadingStep } from "../components/steps/soulmate-loading-step"
import { SoulmateMultiChoiceStep } from "../components/steps/soulmate-multi-choice-step"
import { SoulmatePriceStep } from "../components/steps/soulmate-price-step"
import { SoulmateProcessingStep } from "../components/steps/soulmate-processing-step"
import { SoulmateSelfieStep } from "../components/steps/soulmate-selfie-step"
import { SoulmateSocialProofStep } from "../components/steps/soulmate-social-proof-step"
import type { QuizAnswers, QuizConfig } from "../types"
import { ageFromDob } from "./event-params"

/** Validate that gates Continue on `required` steps (mirrors the shared registry). */
const requiredValidate = (value: QuizAnswerValue | undefined, step: { required?: boolean }) =>
  step.required ? isFilled(value) : true

const has = (answers: QuizAnswers, key: string, value: string) => answers[key] === value

/**
 * "Soulmate Sketch" quiz (Figma Live-Palmistry-4.0, canvas "Quiz Soulmate Sketch",
 * node 2543:1061). Cover → social proof → 15 counted questions → selfie
 * (pass-through; camera is out of scope) → face-scan processing → email →
 * opt-in confirm → loading, then completion routes to the `soulmate` checkout.
 *
 * Extends the shared engine, never forks it: most screens are plain config; the
 * design's divergent rows/scales use `stepOverrides` for single/multiple-choice
 * and the face-scan `analysis`; the rest are one-off `component` screens. Branch
 * reassurance screens (Step8.1 / Step10.1) are gated siblings via `showIf`.
 */
export const soulmateSketchQuiz: QuizConfig = {
  id: "soulmate-sketch",
  name: "Soulmate Sketch",
  steps: [
    // ── Cover ──────────────────────────────────────────────────────────────
    {
      id: "cover",
      eventOnShow: ANALYTICS_EVENTS.SOULMATE_QUIZ_LOADED,
      type: "cover",
      title: "Reveal your ideal partner with your Soulmate Sketch",
      subtitle:
        "Discover who your future partner may be, what kind of connection awaits you, and the signs that could bring you together.",
      features: [
        { icon: "clock", label: "1-minute quiz" },
        { icon: "search", label: "Personalized insights" },
        { icon: "gift", label: "Soulmate guide" },
      ],
      cta: "Take the quiz",
      countsTowardProgress: false,
      component: SoulmateCoverStep,
    },
    // ── Social proof ───────────────────────────────────────────────────────
    {
      id: "social-proof",
      event: ANALYTICS_EVENTS.SOULMATE_QUIZ_USERS_TRUST,
      type: "info",
      title: "Join 14+ million people who've already discovered their true love",
      cta: "Continue",
      countsTowardProgress: false,
      component: SoulmateSocialProofStep,
    },
    // ── Step1 — gender (big icons) ───────────────────────────────────────────
    {
      id: "gender",
      event: ANALYTICS_EVENTS.SOULMATE_QUIZ_1GENDER,
      eventParams: (a) => ({ gender: a.gender }),
      type: "single-choice",
      layout: "icons",
      title: "What's your gender?",
      subtitle: "Your answer helps personalize your soulmate sketch",
      required: true,
      options: [
        { label: "Male", value: "male", icon: "mars" },
        { label: "Female", value: "female", icon: "venus" },
        { label: "Other", value: "other", icon: "ellipsis" },
      ],
    },
    // ── Step2 — partner gender (list + icons) ────────────────────────────────
    {
      id: "partner-gender",
      event: ANALYTICS_EVENTS.SOULMATE_QUIZ_2PARTNER_GENDER,
      eventParams: (a) => ({ gender: a["partner-gender"] }),
      type: "single-choice",
      layout: "list",
      bottomDecoration: true,
      title: "What gender is your ideal partner?",
      required: true,
      options: [
        { label: "Male", value: "male", icon: "mars", iconColor: "text-cyan" },
        { label: "Female", value: "female", icon: "venus", iconColor: "text-peach" },
        { label: "Doesn't matter", value: "any", icon: "waves", iconColor: "text-main-purple" },
      ],
    },
    // ── Step3 — partner age (list) ───────────────────────────────────────────
    {
      id: "partner-age",
      event: ANALYTICS_EVENTS.SOULMATE_QUIZ_3SOULMATE_AGE,
      type: "single-choice",
      layout: "list",
      bottomDecoration: true,
      title: "What age do you think your soulmate should be?",
      required: true,
      options: [
        { label: "20-30", value: "20-30" },
        { label: "30-40", value: "30-40" },
        { label: "40-50", value: "40-50" },
        { label: "50+", value: "50-plus" },
      ],
    },
    // ── Step4 — partner ethnicity (list) ─────────────────────────────────────
    {
      id: "partner-ethnicity",
      event: ANALYTICS_EVENTS.SOULMATE_QUIZ_4ETHNIC,
      type: "single-choice",
      layout: "list",
      bottomDecoration: true,
      title: "What ethnic background should your ideal partner have?",
      required: true,
      options: [
        { label: "African/African-American", value: "african" },
        { label: "Caucasian/White", value: "caucasian" },
        { label: "Hispanic/Latino", value: "hispanic" },
        { label: "Asian", value: "asian" },
        { label: "Doesn't matter", value: "any" },
      ],
    },
    // ── Step5 — date of birth (wheel picker) ─────────────────────────────────
    {
      id: "dob",
      event: ANALYTICS_EVENTS.SOULMATE_QUIZ_5BIRTHDAY,
      eventParams: (a) => ({ age: ageFromDob(a) }),
      type: "date",
      dateMode: "dob",
      title: "What's your date of birth?",
      subtitle: "Your birth date helps reveal your soulmate's portrait",
      required: true,
    },
    // ── Step6 — partner qualities (multi-select) ─────────────────────────────
    {
      id: "partner-qualities",
      event: ANALYTICS_EVENTS.SOULMATE_QUIZ_6QUALITIES,
      type: "multiple-choice",
      title: "What qualities do you look for in your ideal partner?",
      subtitle: "Please select all that apply",
      required: true,
      options: [
        { label: "Kind", value: "kind" },
        { label: "Supportive", value: "supportive" },
        { label: "Honest", value: "honest" },
        { label: "Optimistic", value: "optimistic" },
        { label: "Loyal", value: "loyal" },
        { label: "Caring", value: "caring" },
        { label: "Confident", value: "confident" },
        { label: "Passionate", value: "passionate" },
        { label: "Funny", value: "funny" },
        { label: "Protective", value: "protective" },
      ],
    },
    // ── Step6.2 — "Well noted!" interstitial ─────────────────────────────────
    {
      id: "qualities-noted",
      event: ANALYTICS_EVENTS.SOULMATE_QUIZ_NOTED,
      type: "info",
      title: "Well noted!",
      subtitle:
        "Knowing your future partner's key qualities helps us create a more accurate sketch",
      coverImage: InfoHand,
      countsTowardProgress: false,
      component: SoulmateInterstitialStep,
    },
    // ── Step7 — looks importance (list + icons) ──────────────────────────────
    {
      id: "looks-importance",
      event: ANALYTICS_EVENTS.SOULMATE_QUIZ_7LOOKS,
      type: "single-choice",
      layout: "list",
      bottomDecoration: true,
      title: "How important are looks in a partner for you?",
      required: true,
      options: [
        { label: "Looks really matter", value: "looks-matter", icon: "sparkles" },
        { label: "Nice but not essential", value: "nice-not-essential", icon: "waves" },
        { label: "Personality matters more", value: "personality-more", icon: "heart" },
      ],
    },
    // ── Step8 — emotions or logic (list + icons, branch source) ──────────────
    {
      id: "emotions-or-logic",
      event: ANALYTICS_EVENTS.SOULMATE_QUIZ_8DRIVEN,
      type: "single-choice",
      layout: "list",
      bottomDecoration: true,
      title: "Are you mostly driven by emotions or logic?",
      required: true,
      options: [
        { label: "Emotions", value: "emotions", icon: "heart" },
        { label: "Logic", value: "logic", icon: "brain" },
        { label: "It depends", value: "it-depends", icon: "waves" },
      ],
    },
    // ── Step8.1 — branch reassurance (one shown, keyed on emotions-or-logic) ──
    {
      id: "emotions-reassure",
      event: ANALYTICS_EVENTS.SOULMATE_QUIZ_ADVICE,
      type: "info",
      title: "Listening to your heart opens up new possibilities",
      subtitle:
        "73% of {sign} signs find their ideal partner sooner when they trust their emotions",
      coverImage: InfoHeart,
      countsTowardProgress: false,
      showIf: (a) => has(a, "emotions-or-logic", "emotions"),
      component: SoulmateInterstitialStep,
    },
    {
      id: "logic-reassure",
      event: ANALYTICS_EVENTS.SOULMATE_QUIZ_ADVICE,
      type: "info",
      title: "Keeping a clear head helps you spot red flags early on",
      subtitle: "73% of {sign} signs create lasting relationships when they think clearly",
      coverImage: InfoSun,
      countsTowardProgress: false,
      showIf: (a) => has(a, "emotions-or-logic", "logic"),
      component: SoulmateInterstitialStep,
    },
    {
      id: "balance-reassure",
      event: ANALYTICS_EVENTS.SOULMATE_QUIZ_ADVICE,
      type: "info",
      title: "Balancing emotions and logic leads to wiser choices",
      subtitle: "73% of {sign} signs build fulfilling relationships by trusting both heart and mind",
      coverImage: InfoScale,
      countsTowardProgress: false,
      showIf: (a) => has(a, "emotions-or-logic", "it-depends"),
      component: SoulmateInterstitialStep,
    },
    // ── Step9 — past breakup reason (list) ───────────────────────────────────
    {
      id: "past-breakup-reason",
      event: ANALYTICS_EVENTS.SOULMATE_QUIZ_9REASON,
      type: "single-choice",
      layout: "list",
      bottomDecoration: true,
      title: "What's the biggest reason your past relationships didn't work out?",
      required: true,
      options: [
        { label: "Lack of trust", value: "lack-of-trust" },
        { label: "Poor communication", value: "poor-communication" },
        { label: "Growing apart", value: "growing-apart" },
        { label: "Different life goals", value: "different-life-goals" },
        { label: "Infidelity", value: "infidelity" },
        { label: "Other", value: "other" },
      ],
    },
    // ── Step10 — most difficult (list, branch source) ────────────────────────
    {
      id: "most-difficult",
      event: ANALYTICS_EVENTS.SOULMATE_QUIZ_10DIFFICULT,
      type: "single-choice",
      layout: "list",
      bottomDecoration: true,
      title: "What's most difficult for you?",
      required: true,
      options: [
        { label: "Meeting the right partner", value: "meeting-partner" },
        { label: "Moving on from past relationships", value: "moving-on" },
        { label: "Keeping the relationship exciting", value: "keeping-exciting" },
        { label: "Handling doubts", value: "handling-doubts" },
        { label: "Opening up to people", value: "opening-up" },
        { label: "Setting healthy boundaries", value: "setting-boundaries" },
      ],
    },
    // ── Step10.1 — "Your struggle is real" (one shown, keyed on most-difficult)
    {
      id: "struggle-meeting",
      event: ANALYTICS_EVENTS.SOULMATE_QUIZ_STRUGGLE,
      type: "info",
      title: "Your struggle is real",
      subtitle: "Let's help you discover the path to meeting someone truly compatible with you",
      coverImage: Struggle1,
      countsTowardProgress: false,
      showIf: (a) => has(a, "most-difficult", "meeting-partner"),
      component: SoulmateInterstitialStep,
    },
    {
      id: "struggle-moving-on",
      event: ANALYTICS_EVENTS.SOULMATE_QUIZ_STRUGGLE,
      type: "info",
      title: "Your struggle is real",
      subtitle: "Let's help you release the past and open the way to new love",
      coverImage: Struggle2,
      countsTowardProgress: false,
      showIf: (a) => has(a, "most-difficult", "moving-on"),
      component: SoulmateInterstitialStep,
    },
    {
      id: "struggle-exciting",
      event: ANALYTICS_EVENTS.SOULMATE_QUIZ_STRUGGLE,
      type: "info",
      title: "Your struggle is real",
      subtitle: "Let's help you maintain spark and joy in your love life",
      coverImage: Struggle3,
      countsTowardProgress: false,
      showIf: (a) => has(a, "most-difficult", "keeping-exciting"),
      component: SoulmateInterstitialStep,
    },
    {
      id: "struggle-doubts",
      event: ANALYTICS_EVENTS.SOULMATE_QUIZ_STRUGGLE,
      type: "info",
      title: "Your struggle is real",
      subtitle: "Let's help you gain clarity and confidence in your relationships",
      coverImage: Struggle4,
      countsTowardProgress: false,
      showIf: (a) => has(a, "most-difficult", "handling-doubts"),
      component: SoulmateInterstitialStep,
    },
    {
      id: "struggle-opening-up",
      event: ANALYTICS_EVENTS.SOULMATE_QUIZ_STRUGGLE,
      type: "info",
      title: "Your struggle is real",
      subtitle:
        "Let's help you build trust step by step and create meaningful bonds with others",
      coverImage: Struggle5,
      countsTowardProgress: false,
      showIf: (a) => has(a, "most-difficult", "opening-up"),
      component: SoulmateInterstitialStep,
    },
    {
      id: "struggle-boundaries",
      event: ANALYTICS_EVENTS.SOULMATE_QUIZ_STRUGGLE,
      type: "info",
      title: "Your struggle is real",
      subtitle: "Let's help you understand and honor your needs for balanced relationships",
      coverImage: Struggle6,
      countsTowardProgress: false,
      showIf: (a) => has(a, "most-difficult", "setting-boundaries"),
      component: SoulmateInterstitialStep,
    },
    // ── Step11 — love language (list + icons) ────────────────────────────────
    {
      id: "love-language",
      event: ANALYTICS_EVENTS.SOULMATE_QUIZ_11LANGUAGE,
      type: "single-choice",
      layout: "list",
      bottomDecoration: true,
      title: "What is your love language?",
      required: true,
      options: [
        { label: "Verbal appreciation", value: "verbal", icon: "message" },
        { label: "Presents", value: "presents", icon: "gift" },
        { label: "Shared activities", value: "activities", icon: "activities" },
        { label: "Tactility", value: "tactility", icon: "hand" },
        { label: "Helpful gestures", value: "gestures", icon: "gestures" },
      ],
    },
    // ── Step12 — future vision (multi-select) ────────────────────────────────
    {
      id: "future-vision",
      event: ANALYTICS_EVENTS.SOULMATE_QUIZ_12FUTURE,
      type: "multiple-choice",
      title: "What kind of future do you envision with your ideal partner?",
      subtitle: "Please select all that apply",
      required: true,
      options: [
        { label: "Buying a house", value: "buying-house" },
        { label: "Going on adventures", value: "adventures" },
        { label: "Starting a family", value: "family" },
        { label: "Creating fun memories", value: "memories" },
        { label: "Running a business", value: "business" },
        { label: "Going through challenges together", value: "challenges" },
        { label: "Other", value: "other" },
      ],
    },
    // ── Step13 — partner energy (list) ───────────────────────────────────────
    {
      id: "partner-energy",
      event: ANALYTICS_EVENTS.SOULMATE_QUIZ_13ENERGY,
      type: "single-choice",
      layout: "list",
      bottomDecoration: true,
      title: "What kind of energy does your ideal partner bring into your life?",
      required: true,
      options: [
        { label: "Affectionate and loving", value: "affectionate" },
        { label: "Calm and grounded", value: "calm" },
        { label: "Playful and lighthearted", value: "playful" },
        { label: "Fun and adventurous", value: "fun" },
        { label: "Balanced and supportive", value: "balanced" },
        { label: "Passionate and inspiring", value: "passionate" },
        { label: "Other", value: "other" },
      ],
    },
    // ── Step14 — "opposites attract" Likert ──────────────────────────────────
    {
      id: "opposites-attract",
      event: ANALYTICS_EVENTS.SOULMATE_QUIZ_14BELIEVE,
      type: "single-choice",
      layout: "likert",
      bottomDecoration: true,
      title: "I believe that opposites attract",
      subtitle: "How much do you agree with the statement?",
      required: true,
      options: [
        { label: "Strongly disagree", value: "strongly-disagree" },
        { label: "Disagree", value: "disagree" },
        { label: "Neutral", value: "neutral" },
        { label: "Agree", value: "agree" },
        { label: "Strongly agree", value: "strongly-agree" },
      ],
    },
    // ── Step15 — shared experiences (list + icons) ───────────────────────────
    {
      id: "shared-experiences",
      event: ANALYTICS_EVENTS.SOULMATE_QUIZ_15EXPERIENCE,
      type: "single-choice",
      layout: "list",
      bottomDecoration: true,
      title: "Which shared experiences do you value most?",
      required: true,
      options: [
        { label: "Travelling", value: "travelling", icon: "plane" },
        { label: "Quiet moments together", value: "quiet", icon: "moon" },
        { label: "Celebrating milestones", value: "milestones", icon: "party" },
        { label: "Being creative together", value: "creative", icon: "palette" },
        { label: "Spending time with family", value: "family", icon: "home" },
      ],
    },
    // ── Selfie intro — hero portrait keyed on gender, privacy card, 2 CTAs ────
    {
      id: "selfie",
      type: "info",
      title: "Soulmate insights from you",
      subtitle: "We'll analyze your unique features to create a sketch of your perfect match.",
      countsTowardProgress: false,
      component: SoulmateSelfieStep,
    },
    // ── Face-scan processing ─────────────────────────────────────────────────
    {
      id: "face-analysis",
      eventOnShow: ANALYTICS_EVENTS.SOULMATE_QUIZ_PHOTO_PROCESSING,
      type: "analysis",
      title: "Exploring the unique traits of your face",
      countsTowardProgress: false,
    },
    // ── Email ────────────────────────────────────────────────────────────────
    {
      id: "email",
      event: ANALYTICS_EVENTS.SOULMATE_QUIZ_EMAIL,
      type: "email",
      title: "Are you ready to see what your soulmate looks like?",
      subtitle: "Share your email to get your personalized soulmate sketch.",
      placeholder: "Enter your email",
      required: true,
      legalNote: true,
      submitDelayMs: 1200,
      countsTowardProgress: false,
      component: SoulmateEmailStep,
    },
    // ── Opt-in confirm ───────────────────────────────────────────────────────
    {
      id: "confirm",
      type: "info",
      title:
        "Get access to your Soulmate Sketch, relationship tips, exclusive offers, updates, and free gifts.",
      subtitle: "Just confirm you want to receive emails so you don't miss out on anything.",
      secondaryAction: { label: "No, thanks", value: "declined" },
      submitDelayMs: 1200,
      countsTowardProgress: false,
      component: SoulmateConfirmStep,
    },
    // ── Loading / result hype ────────────────────────────────────────────────
    {
      id: "loading",
      eventOnShow: ANALYTICS_EVENTS.SOULMATE_QUIZ_PROCESSING,
      type: "info",
      title: "Creating your Soulmate Sketch",
      countsTowardProgress: false,
      component: SoulmateLoadingStep,
    },
    // ── Price — pay-what-feels-right amount picker (Figma Price) ──────────────
    // HIDDEN (not removed) per the 2026-06-25 design decision: the price screen is
    // skipped, so the quiz ends at `loading` and routes straight to checkout. The
    // step and `SoulmatePriceStep` stay in the config — `showIf: () => false` drops
    // it from the engine's `visibleSteps`; restore it by deleting this one guard.
    {
      id: "price",
      type: "info",
      title: "Choose a price that feels right for you",
      countsTowardProgress: false,
      showIf: () => false,
      component: SoulmatePriceStep,
    },
  ],
  stepOverrides: {
    // The Soulmate rows diverge from the shared choice steps (no chevron, leading
    // icons, `main-purple` selected fill, plus a big-circle and an emoji-Likert
    // layout) — swap in the dumb Soulmate components; the global registry and the
    // other quizzes are untouched.
    "single-choice": {
      autoAdvance: true,
      validate: requiredValidate,
      Component: SoulmateChoiceStep,
    },
    "multiple-choice": {
      validate: requiredValidate,
      Component: SoulmateMultiChoiceStep,
    },
    // Face-scan analysis: selfie in a dashed ring + a 3-trait checklist, not the
    // shared orb/percentage loader.
    analysis: {
      fullBleed: true,
      validate: () => true,
      Component: SoulmateProcessingStep,
    },
  },
}
